import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public client-facing page shown after a Stripe card-setup Checkout session.
 *
 * URL shapes:
 *   /card-saved?session_id=cs_xxx&company_id=xxx&lead_id=xxx  → processes + shows success
 *   /card-saved?status=cancelled                              → shows cancellation message
 *   /card-saved                                               → plain success (fallback)
 */
export default function CardSaved() {
  const [params]      = useSearchParams();
  const sessionId     = params.get('session_id');
  const companyId     = params.get('company_id');
  const leadId        = params.get('lead_id');
  const cancelled     = params.get('status') === 'cancelled';

  // 'processing' | 'success' | 'error' | 'cancelled'
  const [phase, setPhase]     = useState(() => {
    if (cancelled)  return 'cancelled';
    if (sessionId)  return 'processing';
    return 'success';
  });
  const [cardInfo, setCardInfo] = useState(null);
  const called = useRef(false);

  useEffect(() => {
    if (!sessionId || !companyId || !leadId) return;
    if (called.current) return;   // prevent double-call in StrictMode
    called.current = true;

    fetch(`${API_URL}/api/payments/card-setup-confirm`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ sessionId, companyId, leadId }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.last4) setCardInfo(data);
        setPhase('success');
      })
      .catch(() => {
        // Network/parse error — still show success.
        // Card may have already been saved; don't alarm the client.
        setPhase('success');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const containerStyle = {
    minHeight:       '100vh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    backgroundColor: '#0a0a0a',
  };
  const boxStyle = {
    textAlign:  'center',
    padding:    '2.5rem',
    maxWidth:   '380px',
  };
  const subStyle = { fontSize: '0.875rem', color: '#9ca3af', lineHeight: 1.6 };

  if (phase === 'processing') {
    return (
      <div style={containerStyle} data-testid="card-saved-page">
        <div style={boxStyle}>
          <div style={{ fontSize: '1.5rem', color: '#6b7280', marginBottom: '1rem' }}>
            &#x23F3;
          </div>
          <p style={subStyle} data-testid="card-saved-processing">
            Saving your payment method&hellip;
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'cancelled') {
    return (
      <div style={containerStyle} data-testid="card-saved-page">
        <div style={boxStyle}>
          <div style={{ fontSize: '2.5rem', color: '#6b7280', marginBottom: '1.25rem', lineHeight: 1 }}>
            &#x2715;
          </div>
          <h1
            style={{ fontSize: '1.2rem', fontWeight: 600, color: '#f3f4f6', marginBottom: '0.5rem' }}
            data-testid="card-saved-cancelled-heading"
          >
            Setup Cancelled
          </h1>
          <p style={subStyle}>
            No card was saved. You can safely close this tab or try again later.
          </p>
        </div>
      </div>
    );
  }

  // 'success' (with or without cardInfo)
  return (
    <div style={containerStyle} data-testid="card-saved-page">
      <div style={boxStyle}>
        <div style={{ fontSize: '2.5rem', color: '#10b981', marginBottom: '1.25rem', lineHeight: 1 }}>
          &#x2713;
        </div>
        <h1
          style={{ fontSize: '1.2rem', fontWeight: 600, color: '#f3f4f6', marginBottom: '0.5rem' }}
          data-testid="card-saved-success-heading"
        >
          Card Saved
        </h1>
        {cardInfo ? (
          <p style={subStyle} data-testid="card-saved-details">
            {cardInfo.brand} &bull;&bull;&bull;&bull; {cardInfo.last4} has been securely stored.
            <br />You can safely close this tab.
          </p>
        ) : (
          <p style={subStyle}>
            Your payment method has been securely stored.
            <br />You can safely close this tab.
          </p>
        )}
      </div>
    </div>
  );
}
